import { GUIDE_CHAPTERS, type GuideChapter as SharedChapter } from '@headline/schemas'
import { createFileRoute } from '@tanstack/react-router'
import {
  Building2,
  Compass,
  FileText,
  LayoutTemplate,
  Library,
  LineChart,
  ListChecks,
  Radio,
  Settings,
  ShieldCheck,
  Sparkles,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { PageHeader } from '@/components/PageHeader'
import { cn } from '@/lib/utils'

/**
 * The guide — the app's manual for the Clipbook team that runs it, as a docs page: a sticky table of
 * contents on the left (with scroll-spy highlighting the chapter you're reading) and the chapters on
 * the right. Reached from the Guide link at the foot of the sidebar. Written from the OPERATOR's seat
 * — you work across every account, drill into a customer's, and configure the products they receive —
 * not the customer's; a customer-facing guide is a later concern. Content lives here as data
 * (chapters → topics, some with bullet points) so it stays a scannable reference. Keep it current:
 * when a feature lands or changes, its topic here changes with it.
 */
export const Route = createFileRoute('/app/guide')({
  component: GuidePage,
})

/** The chapters' icons, by the name the shared Guide data gives them. */
const ICONS: Record<string, LucideIcon> = {
  Building2,
  Compass,
  FileText,
  LayoutTemplate,
  Library,
  LineChart,
  ListChecks,
  Radio,
  Settings,
  ShieldCheck,
  Sparkles,
  Zap,
}

type GuideChapter = Omit<SharedChapter, 'icon'> & { icon: LucideIcon }
const CHAPTERS: GuideChapter[] = GUIDE_CHAPTERS.map((chapter) => ({
  ...chapter,
  icon: ICONS[chapter.icon] ?? Compass,
}))

const CHAPTER_IDS = CHAPTERS.map((chapter) => chapter.id)

/** Which chapter is in view — drives the table of contents' active state. Watches each chapter
 *  section with one IntersectionObserver; the topmost one intersecting the reading band wins. */
function useActiveChapter(ids: string[]): string | null {
  const [active, setActive] = useState<string | null>(ids[0] ?? null)
  const key = ids.join('|')
  useEffect(() => {
    const sectionIds = key ? key.split('|') : []
    const observer = new IntersectionObserver(
      (entries) => {
        const top = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
        if (top) setActive(top.target.id)
      },
      // Bias the band below the sticky header, so the active chapter is the one you're reading.
      { rootMargin: '-120px 0px -65% 0px' },
    )
    for (const id of sectionIds) {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    }
    return () => observer.disconnect()
  }, [key])
  return active
}

function GuidePage() {
  const activeId = useActiveChapter(CHAPTER_IDS)

  return (
    <div className="mx-auto w-full max-w-5xl">
      <PageHeader
        title="Guide"
        description="How Clipbook works, for the team that runs it — the two planes, the accounts you operate, and the daily workflows inside them. Use the contents to jump, or read it through."
      />

      {/* Mobile: the contents as a scrollable pill row (the sticky sidebar is desktop-only). */}
      <nav
        aria-label="Guide contents"
        className="-mx-1 mb-6 flex scrollbar-thin gap-2 overflow-x-auto px-1 pb-1 lg:hidden"
      >
        {CHAPTERS.map((chapter) => (
          <a
            key={chapter.id}
            href={`#${chapter.id}`}
            className="shrink-0 rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent/40 hover:text-foreground"
          >
            {chapter.title}
          </a>
        ))}
      </nav>

      <div className="lg:grid lg:grid-cols-[13rem_1fr] lg:gap-10">
        <GuideContents activeId={activeId} />
        <div className="flex min-w-0 flex-col gap-10">
          {CHAPTERS.map((chapter) => (
            <ChapterSection key={chapter.id} chapter={chapter} />
          ))}
          <p className="border-t border-border pt-6 text-sm text-muted-foreground">
            Something out of date, or a workflow that doesn&rsquo;t match what you see? Fix the
            topic here — this guide is meant to track the app.
          </p>
        </div>
      </div>
    </div>
  )
}

/** The sticky table of contents — desktop only; the active chapter gets the brand accent bar, the
 *  same "you are here" treatment the sidebar uses. */
function GuideContents({ activeId }: { activeId: string | null }) {
  return (
    <aside className="hidden lg:block">
      <nav
        aria-label="Guide contents"
        className="sticky top-24 flex flex-col gap-0.5 border-l border-border"
      >
        {CHAPTERS.map((chapter) => {
          const active = chapter.id === activeId
          return (
            <a
              key={chapter.id}
              href={`#${chapter.id}`}
              aria-current={active ? 'true' : undefined}
              className={cn(
                '-ml-px border-l-2 py-1.5 pl-4 text-sm transition-colors',
                active
                  ? 'border-primary font-medium text-foreground'
                  : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
              )}
            >
              {chapter.title}
            </a>
          )
        })}
      </nav>
    </aside>
  )
}

function ChapterSection({ chapter }: { chapter: GuideChapter }) {
  const Icon = chapter.icon
  return (
    // scroll-mt clears the sticky header when a contents link jumps here.
    <section id={chapter.id} className="scroll-mt-24">
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent/60 text-primary">
          <Icon className="size-4.5" />
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold tracking-tight">{chapter.title}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">{chapter.lead}</p>
        </div>
      </div>

      <dl className="mt-4 flex flex-col gap-3">
        {chapter.topics.map((topic) => (
          <div key={topic.title} className="rounded-lg border border-border p-4">
            <dt className="text-sm font-semibold">{topic.title}</dt>
            <dd className="mt-1 text-sm leading-relaxed text-muted-foreground">
              {topic.body}
              {topic.points ? (
                <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 marker:text-muted-foreground/50">
                  {topic.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              ) : null}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  )
}
